/**
 * 🌎 V2X Three.js 环境管理器 - 你的3D世界天空画师
 *
 * 这个文件就像是"电影摄影师的灯光团队"🎬，负责创建和管理3D场景的
 * 天空背景、环境光照，让你的3D世界看起来更真实、更有电影感！
 *
 * 🤔 什么是环境管理？
 * 想象你在拍电影：
 * 1. 🌅 天空背景：决定场景是白天、黄昏还是夜晚
 * 2. 💡 环境光照：来自天空的自然光线，照亮所有物体
 * 3. ✨ 反射效果：金属表面反射出真实的天空
 * 4. 🎨 色彩氛围：整个场景的色调和情绪
 *
 * 🏞️ HDR技术解释：
 * HDR（High Dynamic Range，高动态范围）就像"超级照片"📸：
 * - 普通照片：只能记录有限的明暗范围
 * - HDR照片：可以记录从极暗到极亮的所有细节
 * - 在3D中：提供真实的光照信息，让反射和阴影更自然
 *
 * 🎭 支持的功能：
 * - 🌅 HDR环境贴图加载：真实的天空光照
 * - 🎨 自定义天空盒：渐变色天空效果
 * - 💡 光照强度控制：调节环境光亮度
 * - 🔄 平滑环境切换：在不同环境间过渡
 * - 📦 环境预加载：提前准备多个环境
 */

import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

/**
 * 🌎 环境管理器主类 - 你的3D世界天空大师
 *
 * 这个类就像是"电影布景师"🎭，专门负责设计和管理3D场景的
 * 天空背景和环境光照，让你的3D世界拥有电影级的视觉效果！
 *
 * @example
 * ```typescript
 * // 创建环境管理器
 * const envManager = new EnvironmentManager(scene, renderer);
 *
 * // 加载HDR环境（比如日落天空）
 * await envManager.loadHDREnvironment('sunset.hdr');
 *
 * // 调节光照强度
 * envManager.setIntensity(1.5);  // 让环境光更亮
 *
 * // 创建渐变天空
 * envManager.createGradientSky(0x87ceeb, 0xffffff, 0.6);
 * ```
 */
export class EnvironmentManager {
  // 🎯 核心组件
  private scene: THREE.Scene; // 🎬 Three.js场景（舞台）
  private renderer: THREE.WebGLRenderer; // 🖼️ Three.js渲染器（摄像机）
  private pmremGenerator: THREE.PMREMGenerator; // ⚡ 预过滤多重分辨率环境映射生成器
  private currentEnvMap: THREE.Texture | null = null; // 🌍 当前环境贴图
  private rgbeLoader: RGBELoader; // 📁 RGBE/HDR文件加载器

  // 🎛️ 环境控制参数
  private envIntensity: number = 1.0; // 💡 环境光强度（0-2）
  private backgroundIntensity: number = 0.3; // 🎨 背景强度（0-1）

  /*
   * ⚡ 关于PMREMGenerator的小知识：
   * PMREM = Pre-filtered, Mipmapped Radiance Environment Map
   *
   * 这是一个超级聪明的工具🧠：
   * 1. 📸 把360度全景图转换成立方体贴图
   * 2. 🔍 创建多个分辨率版本（远看用低分辨率，近看用高分辨率）
   * 3. ✨ 预计算光照信息，让反射和环境光更真实
   * 4. 🚀 优化GPU性能，渲染更流畅
   *
   * 就像给照片做"美颜处理"，让普通照片变成专业的光照环境！
   */

  /**
   * 🏗️ 构造函数 - 创建环境管理器
   *
   * 就像搭建一个"摄影棚"🎬，需要准备各种灯光设备和背景布：
   * 1. 🎭 连接到3D场景和渲染器
   * 2. ⚡ 初始化HDR处理工具
   * 3. 🎨 设置默认的环境效果
   *
   * @param scene Three.js场景对象
   * @param renderer Three.js渲染器对象
   */
  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.renderer = renderer;

    // ⚡ 初始化PMREM生成器（HDR魔法师）
    this.pmremGenerator = new THREE.PMREMGenerator(renderer);
    this.pmremGenerator.compileEquirectangularShader(); // 🔧 编译球面投影着色器

    // 📁 初始化RGBE加载器（HDR文件读取器）
    this.rgbeLoader = new RGBELoader();

    // 🎨 设置默认环境
    this.setupDefaultEnvironment();
  }

  /*
   * 🔧 关于compileEquirectangularShader的小知识：
   *
   * Equirectangular = 等距圆柱投影，这是一种地图投影方式：
   * 1. 🗺️ 把球面（地球/天空球）展开成长方形
   * 2. 📸 HDR全景图通常使用这种格式
   * 3. 🔄 需要转换成立方体贴图才能在3D中使用
   * 4. ⚡ 预编译着色器可以加快后续的转换速度
   *
   * 就像把地球仪摊平成世界地图，需要特殊的数学运算！
   */

  /**
   * 🎨 设置默认环境 - 创建基础氛围
   *
   * 这个方法就像"电影开拍前的基础布光"💡，设置一个基本的
   * 环境氛围，确保即使没有HDR贴图，场景也有合适的外观！
   */
  private setupDefaultEnvironment(): void {
    // 🎨 设置清晰的深蓝色背景（像深夜的天空）
    this.scene.background = new THREE.Color(0x0f0f23); // 深蓝色 = RGB(15, 15, 35)
    this.scene.fog = null; // 🌫️ 明确移除雾效（确保视野清晰）

    // 🎬 设置渲染器参数以优化HDR显示
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping; // 🎭 ACES电影色调映射
    this.renderer.toneMappingExposure = 1.0; // 📷 曝光度设置
    this.renderer.outputColorSpace = THREE.SRGBColorSpace; // 🎨 输出颜色空间
  }

  /*
   * 🎬 关于色调映射（Tone Mapping）的小知识：
   *
   * HDR图像包含的亮度范围比显示器能显示的要大得多：
   * - ☀️ 太阳的亮度 vs 🌙 月亮的亮度 差距巨大
   * - 💻 显示器只能显示有限的亮度范围
   * - 🎭 色调映射就是"压缩"这个范围的艺术
   *
   * ACES Filmic Tone Mapping：
   * - 🎬 电影行业标准的色调映射算法
   * - 🌟 保持高光细节，避免过曝
   * - 🎨 产生更自然、电影感的色彩
   *
   * 就像摄影师调节相机参数，让照片既不过暗也不过亮！
   */

  /**
   * 🌅 加载HDR环境贴图 - 导入真实世界的光照
   *
   * 这个方法就像"导入专业摄影师拍摄的360度全景照片"📸，
   * 让你的3D世界拥有真实世界的光照和反射效果！
   *
   * @param url HDR文件的网络地址或本地路径
   * @param onProgress 可选的加载进度回调函数
   * @returns Promise<THREE.Texture> 处理好的环境贴图
   */
  public async loadHDREnvironment(
    url: string,
    onProgress?: (progress: ProgressEvent) => void
  ): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      // 📁 使用RGBE加载器加载HDR文件
      this.rgbeLoader.load(
        url,
        (texture: any) => {
          // ⚡ 将HDR纹理转换为环境贴图
          const envMap =
            this.pmremGenerator.fromEquirectangular(texture).texture;

          // 🌍 设置为场景环境
          this.setEnvironmentMap(envMap);

          // 🧹 清理临时纹理（释放内存）
          texture.dispose();

          console.log('[Environment Manager] HDR环境贴图加载成功'); // 📝 成功日志
          resolve(envMap); // ✅ 返回处理好的环境贴图
        },
        onProgress, // 📊 传递进度回调
        (error: any) => {
          console.error('[Environment Manager] HDR环境贴图加载失败:', error); // ❌ 错误日志
          reject(error); // ❌ 抛出错误
        }
      );
    });
  }

  /*
   * 📁 关于RGBE文件格式的小知识：
   *
   * RGBE = Red Green Blue Exponent（红绿蓝指数）：
   * 1. 📸 一种HDR图像格式，扩展名通常是.hdr
   * 2. 💾 用4个字节存储RGB颜色 + 一个指数
   * 3. ✨ 可以表示极大的亮度范围（比如太阳的亮度）
   * 4. 🌍 常用于存储环境光照信息
   *
   * 就像把"超级照片"压缩成合理大小的文件，但保留所有光照细节！
   */

  /**
   * 🌍 设置环境贴图 - 更换3D世界的天空
   *
   * 这个方法就像"更换电影拍摄的背景布景"🎬，把新的环境贴图
   * 应用到3D场景中，立刻改变整个世界的光照和氛围！
   *
   * @param envMap 新的环境贴图纹理
   */
  public setEnvironmentMap(envMap: THREE.Texture): void {
    // 🧹 清理旧的环境贴图（防止内存泄漏）
    if (this.currentEnvMap) {
      this.currentEnvMap.dispose(); // 释放GPU内存
    }

    this.currentEnvMap = envMap; // 💾 保存新的环境贴图引用

    // 🌍 将环境贴图应用到场景
    this.scene.environment = envMap; // 💡 用于环境光照和反射
    this.scene.background = envMap; // 🎨 用作天空背景

    // ⚡ 应用当前的强度设置
    this.updateEnvironmentIntensity(); // 🔆 更新环境光强度
    this.updateBackgroundIntensity(); // 🎨 更新背景强度
  }

  /*
   * 🌍 关于环境贴图应用的小知识：
   *
   * scene.environment vs scene.background：
   * 1. 💡 environment：影响物体的光照和反射
   *    - 金属表面会反射环境中的景物
   *    - 为所有材质提供环境光照
   *
   * 2. 🎨 background：作为场景的背景显示
   *    - 当相机看向没有物体的地方时显示
   *    - 不一定要和environment相同
   *
   * 通常设置为同一个贴图，保持视觉一致性！
   */

  /**
   * 💡 设置环境光强度 - 调节世界的亮度
   *
   * 这个方法就像"调节摄影棚的灯光亮度"💡，让你可以
   * 让场景更亮（白天感）或更暗（夜晚感）！
   *
   * @param intensity 强度值，范围0-2（0=全黑，1=正常，2=超亮）
   */
  public setIntensity(intensity: number): void {
    // 🎚️ 限制数值范围在0-2之间
    this.envIntensity = Math.max(0, Math.min(2, intensity));
    this.updateEnvironmentIntensity(); // 🔄 立即应用新强度
  }

  /**
   * 🎨 设置背景强度 - 调节天空的亮度
   *
   * 这个方法就像"调节背景灯的亮度"🎨，可以让天空背景
   * 更突出或更低调，而不影响物体的光照效果！
   *
   * @param intensity 背景强度值，范围0-1（0=全暗，1=正常）
   */
  public setBackgroundIntensity(intensity: number): void {
    // 🎚️ 限制数值范围在0-1之间
    this.backgroundIntensity = Math.max(0, Math.min(1, intensity));
    this.updateBackgroundIntensity(); // 🔄 立即应用新强度
  }

  /**
   * 🔆 更新环境光强度 - 应用光照强度变化
   *
   * 这个方法就像"实际转动灯光调节旋钮"💡，通过调节
   * 渲染器的曝光度来控制整个场景的亮度！
   */
  private updateEnvironmentIntensity(): void {
    if (this.scene.environment) {
      // 📷 通过调整tone mapping exposure来控制环境光强度
      this.renderer.toneMappingExposure = this.envIntensity;
    }
  }

  /*
   * 📷 关于曝光度（Exposure）的小知识：
   *
   * 在摄影中，曝光度控制进入相机的光量：
   * 1. 📈 曝光度↑ = 照片更亮
   * 2. 📉 曝光度↓ = 照片更暗
   * 3. 🎯 toneMappingExposure就是3D渲染中的"曝光度"
   * 4. ⚡ 实时调节，立即看到效果
   *
   * 就像相机的光圈和快门速度控制，但更简单直接！
   */

  /**
   * 🎨 更新背景强度 - 应用背景亮度变化
   *
   * 这个方法负责调节背景的显示强度，不过目前通过
   * 环境光强度来间接控制（保持视觉一致性）。
   */
  private updateBackgroundIntensity(): void {
    if (this.scene.background instanceof THREE.Texture) {
      // 🎨 可以通过调整背景的不透明度或者色彩混合来控制
      // 🔄 这里我们通过调整环境强度来间接控制
      // 💡 未来可以添加更精细的背景控制
    }
  }

  /**
   * 📊 获取当前环境强度
   *
   * @returns number 当前环境光强度值
   */
  public getIntensity(): number {
    return this.envIntensity;
  }

  /**
   * 📊 获取当前背景强度
   *
   * @returns number 当前背景强度值
   */
  public getBackgroundIntensity(): number {
    return this.backgroundIntensity;
  }

  /**
   * 🔄 切换背景显示模式 - 更换天空类型
   *
   * 这个方法就像"更换舞台背景"🎭，可以在不同类型的
   * 背景之间切换：真实环境、纯色背景、或透明背景！
   *
   * @param mode 显示模式：'environment'(环境贴图) | 'color'(纯色) | 'none'(透明)
   * @param color 当模式为'color'时使用的颜色（十六进制数值）
   */
  public setBackgroundMode(
    mode: 'environment' | 'color' | 'none',
    color?: number
  ): void {
    switch (mode) {
      case 'environment':
        // 🌍 使用HDR环境贴图作为背景
        if (this.currentEnvMap) {
          this.scene.background = this.currentEnvMap;
        }
        break;
      case 'color':
        // 🎨 使用纯色作为背景
        this.scene.background = new THREE.Color(color ?? 0x0f0f23); // 默认深蓝色
        break;
      case 'none':
        // 👻 透明背景（可以看到网页内容）
        this.scene.background = null;
        break;
    }
  }

  /*
   * 🎭 关于背景模式的小知识：
   *
   * 1. 🌍 Environment模式：
   *    - 显示HDR环境贴图
   *    - 提供真实的反射和光照
   *    - 适合写实渲染
   *
   * 2. 🎨 Color模式：
   *    - 显示纯色背景
   *    - 简洁干净的外观
   *    - 适合产品展示
   *
   * 3. 👻 None模式：
   *    - 透明背景
   *    - 可以看到HTML页面内容
   *    - 适合嵌入网页的3D元素
   *
   * 就像拍照时选择不同的背景布！
   */

  /**
   * 🎨 创建自定义天空盒 - 制作渐变天空
   *
   * 这个方法就像"画家绘制天空"🎨，使用GPU着色器创建
   * 从天顶到地平线的美丽渐变效果，比如日出日落的天空！
   *
   * @param color1 天顶颜色（天空最高处的颜色，默认天蓝色）
   * @param color2 地平线颜色（天空边缘的颜色，默认浅绿色）
   * @param exponent 渐变指数（控制颜色过渡的陡峭程度，默认0.6）
   */
  public createGradientSky(
    color1: number = 0x87ceeb, // 🌅 天蓝色 (Sky Blue)
    color2: number = 0x98fb98, // 🌱 浅绿色 (Pale Green)
    exponent: number = 0.6 // 📐 渐变指数
  ): void {
    // 🎨 顶点着色器：处理3D位置变换
    const vertexShader = `
      varying vec3 vWorldPosition;  // 传递给片段着色器的世界坐标
      void main() {
        // 🌍 计算顶点在世界空间中的位置
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        
        // 📷 计算最终的屏幕位置
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    // 🎨 片段着色器：计算每个像素的颜色
    const fragmentShader = `
      uniform vec3 topColor;       // 天顶颜色
      uniform vec3 bottomColor;    // 地平线颜色
      uniform float offset;        // 位置偏移
      uniform float exponent;      // 渐变指数
      varying vec3 vWorldPosition; // 从顶点着色器传来的世界坐标
      
      void main() {
        // 📐 计算垂直高度比例（Y轴）
        float h = normalize(vWorldPosition + offset).y;
        
        // 🎨 根据高度在两个颜色之间插值
        gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
      }
    `;

    // 🎛️ 着色器参数
    const uniforms = {
      topColor: { value: new THREE.Color(color1) }, // 🌅 天顶颜色
      bottomColor: { value: new THREE.Color(color2) }, // 🌱 地平线颜色
      offset: { value: 33 }, // 📍 位置偏移值
      exponent: { value: exponent }, // 📐 渐变指数
    };

    // 🌐 创建球形几何体作为天空盒
    const skyGeo = new THREE.SphereGeometry(1000, 32, 15); // 半径1000的球体

    // 🎨 创建着色器材质
    const skyMat = new THREE.ShaderMaterial({
      uniforms: uniforms, // 传入参数
      vertexShader: vertexShader, // 顶点着色器
      fragmentShader: fragmentShader, // 片段着色器
      side: THREE.BackSide, // 🔄 从内部渲染（我们在球体内部）
    });

    // ✨ 创建天空球体并添加到场景
    const sky = new THREE.Mesh(skyGeo, skyMat);
    this.scene.add(sky);
  }

  /*
   * 🎨 关于GPU着色器的小知识：
   *
   * 着色器是在显卡上运行的小程序：
   * 1. 📐 顶点着色器：处理3D坐标变换
   * 2. 🎨 片段着色器：计算每个像素的颜色
   * 3. ⚡ GPU并行计算，速度极快
   * 4. 🔥 可以创造各种酷炫的视觉效果
   *
   * mix()函数：颜色混合
   * - mix(A, B, 0.0) = 100% A颜色
   * - mix(A, B, 0.5) = 50% A + 50% B
   * - mix(A, B, 1.0) = 100% B颜色
   *
   * pow()函数：幂运算，控制渐变曲线
   * - pow(x, 1) = 线性渐变
   * - pow(x, 0.6) = 缓慢开始，快速结束
   * - pow(x, 2) = 快速开始，缓慢结束
   *
   * 就像Photoshop的渐变工具，但运行在GPU上更快！
   */

  /**
   * 📦 预加载多个HDR环境 - 提前准备环境库
   *
   * 这个方法就像"电影剧组提前准备多套背景布景"🎬，
   * 把多个HDR环境一次性加载好，方便后续快速切换！
   *
   * @param urls HDR文件URL数组
   * @param onProgress 总体加载进度回调函数
   * @returns Promise<THREE.Texture[]> 所有加载成功的环境贴图数组
   */
  public async preloadHDREnvironments(
    urls: string[],
    onProgress?: (loadedCount: number, total: number) => void
  ): Promise<THREE.Texture[]> {
    const envMaps: THREE.Texture[] = []; // 📦 存储加载好的环境贴图

    // 🔄 依次加载每个HDR文件
    for (let i = 0; i < urls.length; i++) {
      try {
        const envMap = await this.loadHDREnvironment(urls[i]); // 📁 加载单个HDR
        envMaps.push(envMap); // ➕ 添加到数组

        if (onProgress) {
          onProgress(i + 1, urls.length); // 📊 报告进度
        }
      } catch (error) {
        console.warn(`[Environment Manager] 预加载HDR失败: ${urls[i]}`, error); // ⚠️ 记录失败
        // 💪 继续加载其他文件，不因单个失败而停止
      }
    }

    return envMaps; // 📦 返回所有成功加载的环境贴图
  }

  /**
   * 🔄 平滑切换环境贴图 - 电影级环境转场
   *
   * 这个方法就像"电影中的场景转换特效"🎬，让环境切换
   * 不是生硬的跳跃，而是优雅的淡入淡出过渡！
   *
   * @param newEnvMap 要切换到的新环境贴图
   * @param duration 切换持续时间（毫秒，默认1000ms=1秒）
   * @returns Promise<void> 当切换完成时resolve
   */
  public async transitionToEnvironment(
    newEnvMap: THREE.Texture,
    duration: number = 1000
  ): Promise<void> {
    return new Promise(resolve => {
      const startTime = Date.now(); // ⏰ 记录开始时间
      const startIntensity = this.envIntensity; // 💾 记录初始强度

      // 🎬 动画函数
      const animate = () => {
        const elapsed = Date.now() - startTime; // ⏱️ 已过时间
        const progress = Math.min(elapsed / duration, 1); // 📊 进度百分比(0-1)

        if (progress < 0.5) {
          // 🌅 前半段：淡出当前环境
          this.setIntensity(startIntensity * (1 - progress * 2));
        } else {
          // 🌇 后半段：切换并淡入新环境
          if (progress === 0.5) {
            this.setEnvironmentMap(newEnvMap); // 🔄 在中点切换环境
          }
          this.setIntensity(startIntensity * ((progress - 0.5) * 2));
        }

        if (progress < 1) {
          requestAnimationFrame(animate); // 🔄 继续动画
        } else {
          resolve(); // ✅ 动画完成
        }
      };

      animate(); // 🚀 开始动画
    });
  }

  /*
   * 🎬 关于动画原理的小知识：
   *
   * 这个过渡动画分为两个阶段：
   * 1. 🌅 前半段(0%-50%)：淡出旧环境
   *    - 强度从100%降到0%
   *    - progress * 2 让前半段占满0-1范围
   *
   * 2. 🌇 后半段(50%-100%)：淡入新环境
   *    - 在50%时刻切换环境贴图
   *    - 强度从0%升到100%
   *    - (progress - 0.5) * 2 让后半段占满0-1范围
   *
   * requestAnimationFrame：
   * - 🖥️ 与显示器刷新率同步（通常60FPS）
   * - ⚡ 比setTimeout更流畅
   * - 🔋 页面不可见时自动暂停，节省电量
   *
   * 就像电影中的溶解转场效果！
   */

  /**
   * 📊 获取环境信息 - 当前状态报告
   *
   * 这个方法就像"环境管理器的状态仪表盘"📈，
   * 返回当前环境的所有重要信息！
   *
   * @returns 包含环境状态的对象
   */
  public getEnvironmentInfo(): {
    hasEnvironment: boolean; // 🌍 是否有环境贴图
    intensity: number; // 💡 环境光强度
    backgroundIntensity: number; // 🎨 背景强度
    backgroundMode: string; // 🔄 背景模式
  } {
    return {
      hasEnvironment: this.currentEnvMap !== null, // 🔍 检查是否有环境贴图
      intensity: this.envIntensity, // 💡 当前环境光强度
      backgroundIntensity: this.backgroundIntensity, // 🎨 当前背景强度
      backgroundMode: this.scene.background ? 'environment' : 'color', // 🎭 当前背景模式
    };
  }

  /**
   * 🗑️ 销毁环境管理器 - 彻底清理资源
   *
   * 这个方法就像"拍摄结束后的片场清理"🧹，释放所有
   * GPU内存和资源，防止内存泄漏！
   */
  public dispose(): void {
    // 🧹 清理当前环境贴图
    if (this.currentEnvMap) {
      this.currentEnvMap.dispose(); // 释放GPU纹理内存
    }

    // 🧹 清理PMREM生成器
    this.pmremGenerator.dispose(); // 释放相关GPU资源

    // 🧹 清理RGBE加载器引用
    this.rgbeLoader = null as any; // 清空引用，帮助垃圾回收
  }
}

/*
 * 🎉 总结：环境管理器为你提供了什么？
 *
 * 1. 🌅 真实环境光照：
 *    - HDR全景图加载和处理
 *    - 真实的反射和环境光
 *    - 电影级的视觉效果
 *    - 优化的GPU性能
 *
 * 2. 🎨 多样化背景选择：
 *    - HDR环境贴图背景
 *    - 自定义渐变天空
 *    - 纯色背景
 *    - 透明背景
 *
 * 3. 💡 灵活的光照控制：
 *    - 实时调节环境光强度
 *    - 独立的背景亮度控制
 *    - 平滑的环境切换动画
 *    - 专业的色调映射
 *
 * 4. 📦 高效的资源管理：
 *    - 环境贴图预加载
 *    - 内存泄漏预防
 *    - GPU资源优化
 *    - 完善的清理机制
 *
 * 5. 🎬 专业级工具：
 *    - ACES电影色调映射
 *    - PMREM环境预过滤
 *    - GPU着色器编程
 *    - 实时环境信息监控
 *
 * 💭 使用场景：
 * 这个环境管理器让你的3D场景拥有电影级的光照和氛围，
 * 适用于建筑可视化、产品展示、游戏开发等需要高质量视觉效果的项目！
 *
 * 🎯 最佳实践：
 * 1. 先设置基础环境，再加载HDR
 * 2. 使用预加载功能准备多个环境
 * 3. 利用平滑切换创造动态效果
 * 4. 记得在不需要时调用dispose()清理资源
 */
